import React, { useState, useEffect } from 'react';
import { Phone, Star, Shield, Clock, Users, MapPin, Award, ChevronRight, Menu, X, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import EmergencyBailAssistanceForm from '../landing-page/components/EmergencyBailAssistanceForm';

const HomepageRedesign = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [logoAnimating, setLogoAnimating] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Testimonials data
  const testimonials = [
    {
      name: "Alex Johnson",
      rating: 5,
      text: "They turned a stressful night around—super grateful! Professional and caring service.",
      date: "2 weeks ago"
    },
    {
      name: "Maria Rodriguez",
      rating: 5,
      text: "Helped my brother get out quickly. Explained everything clearly and made the process smooth.",
      date: "1 month ago"
    },
    {
      name: "David Chen",
      rating: 5,
      text: "24/7 availability saved us. Called at 3 AM and they answered immediately with solutions.",
      date: "3 weeks ago"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white font-sans">
      {/* Sticky Header Navigation */}
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-sm'
        }`}
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo with Animation */}
            <Link to="/" className="flex items-center space-x-3">
              <div className={`relative ${logoAnimating ? 'animate-sway' : ''}`}>
                <img 
                  src="/assets/images/162111a8-97b7-4fd6-a950-171d24f8aaac-1764521073501.png" 
                  alt="Animated Sailors Bail Bonds anchor logo with rope"
                  className="h-16 w-auto"
                  onMouseEnter={() => setLogoAnimating(true)}
                  onMouseLeave={() => setLogoAnimating(false)}
                />
              </div>
            </Link>

            {/* Client Portal Access Button - Desktop */}
            <div className="hidden lg:block">
              <Link 
                to="/client-portal-dashboard"
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-3xl font-medium transition-all transform hover:scale-105 shadow-md flex items-center"
              >
                <User className="w-4 h-4 mr-2" />
                Client Access
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              <a 
                href="#hero" 
                className="text-gray-700 hover:text-blue-600 font-medium transition-colors cursor-pointer"
                onClick={(e) => {
                  e?.preventDefault();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Home
              </a>
              <Link to="/about-us" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                About
              </Link>
              <Link to="/services" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                Services
              </Link>
              <Link to="/locations" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                Locations
              </Link>
              <Link to="/blog" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                Blog
              </Link>
              <Link 
                to="/appointment-scheduling-system" 
                className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
              >
                Schedule
              </Link>
              <Link to="/contact-us" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                Contact
              </Link>
              
              {/* Emergency Button */}
              <a 
                href="tel:9162563468"
                className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-6 py-3 rounded-xl font-bold transition-all transform hover:scale-105 shadow-lg flex items-center"
              >
                <Phone className="w-4 h-4 mr-2" />
                24/7 Emergency Bail
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Toggle mobile menu"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-gray-700" />
              ) : (
                <Menu className="w-6 h-6 text-gray-700" />
              )}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="lg:hidden py-4 border-t border-gray-200">
              <div className="flex flex-col space-y-4">
                {/* Client Portal Access Button - Mobile */}
                <Link 
                  to="/client-portal-dashboard"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-3xl font-medium text-center flex items-center justify-center"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <User className="w-4 h-4 mr-2" />
                  Client Access
                </Link>
                
                <a 
                  href="#hero" 
                  className="text-gray-700 hover:text-blue-600 font-medium transition-colors cursor-pointer"
                  onClick={(e) => {
                    e?.preventDefault();
                    setMobileMenuOpen(false);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  Home
                </a>
                <Link to="/about-us" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                  About
                </Link>
                <Link to="/services" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                  Services
                </Link>
                <Link to="/locations" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                  Locations
                </Link>
                <Link to="/blog" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                  Blog
                </Link>
                <Link 
                  to="/appointment-scheduling-system" 
                  className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Schedule
                </Link>
                <Link to="/contact-us" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                  Contact
                </Link>
                <a 
                  href="tel:9162563468"
                  className="bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-3 rounded-xl font-bold text-center"
                >
                  <Phone className="w-4 h-4 inline mr-2" />
                  24/7 Emergency Bail
                </a>
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Company Name and License Banner */}
      <div className="fixed top-20 left-0 right-0 z-40 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-1" style={{ fontFamily: 'Merriweather, serif', letterSpacing: '0.05em' }}>
              SAILORS BAIL BONDS
            </h2>
            <p className="text-lg sm:text-xl font-bold text-blue-200 tracking-wider">
              CA LIC# 1N13041
            </p>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden" style={{ marginTop: '80px' }}>
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-teal-50 opacity-50"></div>
        
        <div className="relative max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Hero Content */}
            <div className="text-center lg:text-left">
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-gray-900 mb-6 leading-tight" style={{ fontFamily: 'Merriweather, serif' }}>
                Tough spot? We're here around the clock to make bail straightforward and supportive.
              </h1>
              
              <p className="text-xl text-gray-700 mb-8 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                We understand how overwhelming bail can feel—our team is here to chat you through it with kindness and clarity in Sacramento, Stockton, and Modesto.
              </p>

              {/* Quick Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
                <a 
                  href="tel:9162563468"
                  className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105 shadow-lg flex items-center justify-center"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Call Now: (916) 256-3468
                </a>
                
                <a 
                  href="#emergency-form"
                  className="bg-white border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center"
                >
                  Get Started Online
                  <ChevronRight className="w-5 h-5 ml-2" />
                </a>
              </div>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-6 justify-center lg:justify-start">
                <div className="flex items-center text-gray-700">
                  <Shield className="w-5 h-5 mr-2 text-blue-600" />
                  <span className="font-medium">Bonded & Licensed</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <Award className="w-5 h-5 mr-2 text-blue-600" />
                  <span className="font-medium">15+ Years Experience</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <Clock className="w-5 h-5 mr-2 text-blue-600" />
                  <span className="font-medium">2-Minute Response</span>
                </div>
              </div>
            </div>

            {/* Hero Image/Stats */}
            <div className="relative">
              <div className="bg-gradient-to-br from-blue-100 to-teal-100 rounded-3xl p-8 shadow-2xl">
                <div className="grid grid-cols-2 gap-6">
                  <div className="bg-white rounded-2xl p-6 text-center transform hover:scale-105 transition-transform">
                    <div className="text-4xl font-black text-blue-600 mb-2">2min</div>
                    <div className="text-gray-700 font-semibold">Response Time</div>
                  </div>
                  <div className="bg-white rounded-2xl p-6 text-center transform hover:scale-105 transition-transform">
                    <div className="text-4xl font-black text-green-600 mb-2">24/7</div>
                    <div className="text-gray-700 font-semibold">Available</div>
                  </div>
                  <div className="bg-white rounded-2xl p-6 text-center transform hover:scale-105 transition-transform">
                    <div className="text-4xl font-black text-purple-600 mb-2">100%</div>
                    <div className="text-gray-700 font-semibold">Confidential</div>
                  </div>
                  <div className="bg-white rounded-2xl p-6 text-center transform hover:scale-105 transition-transform">
                    <div className="text-4xl font-black text-orange-600 mb-2">98%</div>
                    <div className="text-gray-700 font-semibold">Success Rate</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Services Overview Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-gray-900 mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
              How We Help You Navigate Bail
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              At Sailors Bail Bonds, we're your reliable crew for navigating bail challenges.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Service Card 1 */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 hover:shadow-xl transition-shadow border-2 border-blue-200">
              <div className="bg-blue-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <Clock className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">24/7 Emergency Help</h3>
              <p className="text-gray-700 mb-6">
                Day or night, we're always available. No matter when trouble strikes, our team answers the call.
              </p>
              <Link 
                to="/services"
                className="text-blue-600 font-bold flex items-center hover:underline"
              >
                Dive Deeper
                <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </div>

            {/* Service Card 2 */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-8 hover:shadow-xl transition-shadow border-2 border-green-200">
              <div className="bg-green-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Family-Focused Support</h3>
              <p className="text-gray-700 mb-6">
                We treat your family like our own. Compassionate guidance through every step of the process.
              </p>
              <Link 
                to="/services"
                className="text-green-600 font-bold flex items-center hover:underline"
              >
                Dive Deeper
                <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </div>

            {/* Service Card 3 */}
            <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-8 hover:shadow-xl transition-shadow border-2 border-teal-200">
              <div className="bg-teal-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <MapPin className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Local Expertise</h3>
              <p className="text-gray-700 mb-6">
                Sacramento, Stockton, Modesto—we know the local jails and courts inside out.
              </p>
              <Link 
                to="/locations"
                className="text-teal-600 font-bold flex items-center hover:underline"
              >
                Dive Deeper
                <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
          </div>
        </div>
      </section>
      {/* Testimonials Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-gray-900 mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-600">
              Real stories from families we've helped
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials?.map((testimonial, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow border-2 border-gray-200"
              >
                <div className="flex items-center mb-4">
                  {[...Array(testimonial?.rating)]?.map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">
                  "{testimonial?.text}"
                </p>
                <div className="border-t pt-4">
                  <p className="font-bold text-gray-900">{testimonial?.name}</p>
                  <p className="text-sm text-gray-500">{testimonial?.date}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Emergency Form Section */}
      <section id="emergency-form" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black text-gray-900 mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
              Let's Get Started
            </h2>
            <p className="text-xl text-gray-600">
              Fill out this form and we'll contact you within 2 minutes
            </p>
          </div>
          
          {/* Preserved Emergency Form */}
          <EmergencyBailAssistanceForm />
        </div>
      </section>
      {/* Footer */}
      <footer className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            {/* Company Info */}
            <div>
              <img 
                src="/assets/images/162111a8-97b7-4fd6-a950-171d24f8aaac-1764521073501.png" 
                alt="Sailors Bail Bonds logo"
                className="h-16 w-auto mb-4"
              />
              <p className="text-gray-300 mb-4">
                901 H Street, Suite 103<br />
                Sacramento, CA 95814
              </p>
              <a 
                href="tel:9162563468"
                className="text-2xl font-bold text-white hover:text-blue-400 transition-colors"
              >
                (916) 256-3468
              </a>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-xl font-bold mb-6">Quick Links</h3>
              <ul className="space-y-3">
                <li>
                  <a 
                    href="#hero" 
                    className="text-gray-300 hover:text-white transition-colors cursor-pointer"
                    onClick={(e) => {
                      e?.preventDefault();
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  >
                    Home
                  </a>
                </li>
                <li><Link to="/about-us" className="text-gray-300 hover:text-white transition-colors">About Us</Link></li>
                <li><Link to="/services" className="text-gray-300 hover:text-white transition-colors">Services</Link></li>
                <li><Link to="/locations" className="text-gray-300 hover:text-white transition-colors">Locations</Link></li>
                <li><Link to="/blog" className="text-gray-300 hover:text-white transition-colors">Blog</Link></li>
                <li><Link to="/appointment-scheduling-system" className="text-gray-300 hover:text-white transition-colors">Schedule</Link></li>
                <li><Link to="/contact-us" className="text-gray-300 hover:text-white transition-colors">Contact</Link></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h3 className="text-xl font-bold mb-6">Legal</h3>
              <ul className="space-y-3">
                <li><Link to="/privacy-policy" className="text-gray-300 hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms-and-conditions" className="text-gray-300 hover:text-white transition-colors">Terms of Service</Link></li>
                <li><Link to="/legal-disclaimer" className="text-gray-300 hover:text-white transition-colors">Disclaimer</Link></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h3 className="text-xl font-bold mb-6">Get Help Now</h3>
              <a 
                href="tel:9162563468"
                className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-6 py-3 rounded-xl font-bold inline-flex items-center transition-all transform hover:scale-105 mb-6"
              >
                <Phone className="w-4 h-4 mr-2" />
                Emergency Call
              </a>
              <p className="text-gray-300 text-sm">
                Available 24/7 for emergencies
              </p>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-8 text-center">
            <p className="text-gray-400 mb-4">
              © 2025 Sailors Bail Bonds. All Rights Reserved.
            </p>
            <p className="text-gray-500 text-sm">
              Committed to inclusivity—let us know how we can improve.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomepageRedesign;